import { TrashIcon } from '@radix-ui/react-icons'
import { useRuntimeVariables } from 'app-kit'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { useGeckoAnalytics } from 'features/analytics'
import { useGeckoAuth } from 'features/auth'
import { useKestrel } from 'features/kestrelViewer'
import { ViewToggle } from 'features/kestrelViewer/ui/tools/ViewToggle'
import React, { useState } from 'react'

export const DeleteVMButton: React.FC = () => {
  const { kestrelModel, setPixelStreamingURL, setKestrelStatus } = useKestrel()
  const { authorization } = useGeckoAuth()
  const { trackButtonClicked } = useGeckoAnalytics()
  const { KESTREL_MATCHMAKER_URL } = useRuntimeVariables()
  const deleteURL = `${KESTREL_MATCHMAKER_URL}/api/v1/kestrel/delete-vm-by-model-id`
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deleteVM = async () => {
    if (isLoading) return // Prevent multiple clicks while loading
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(deleteURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${authorization}`,
        },
        body: JSON.stringify({
          kestrelModelId: kestrelModel?.data.kestrel_model_id,
        }),
      })

      if (!response.ok) {
        throw new Error(`Failed to delete VM: ${response.statusText}`)
      }

      // Reset Kestrel state and clear pixel streaming URL after successful deletion
      setPixelStreamingURL('')
      setKestrelStatus({
        toggledOn: false,
        shouldConnect: false,
        videoReady: false,
      })
    } catch (error) {
      console.error('Error connecting to matchmaker:', error)
      setError(
        error instanceof Error ? error.message : 'An unknown error occurred'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleClick = () => {
    deleteVM()
    trackButtonClicked({
      button_name: 'kestrel_delete_vm',
      kestrel_model_id: kestrelModel?.data.kestrel_model_id,
    })
  }

  const getTooltipLabel = () => {
    if (error) return `Error: ${error}`
    if (isLoading) return 'Deleting VM...'
    return 'Delete VM'
  }

  return (
    <TooltipWrapper side={'right'} offset={5} label={getTooltipLabel()}>
      <ViewToggle onClick={handleClick}>
        <TrashIcon
          width={22}
          height={22}
          style={{ opacity: isLoading || error ? 0.5 : 1 }}
        />
      </ViewToggle>
    </TooltipWrapper>
  )
}
